import { useId } from 'preact/hooks';
import { TROPHY_METALS, ROMAN_RANKS } from '../utils/arena/trophies';
import { t } from '../localization/t';

// Podium prize badge: a tall heater shield forged in gold, silver or
// bronze with a pendant loop, an aged enamel field, and the Mjölnir
// ornament raised in the same metal, the rank struck as a Roman numeral
// on a boss at the point.
//
// Everything is one inline SVG so it scales losslessly with the canvas.
// Depth comes from SVG lighting rather than painted gradients alone: the
// `light` filter blurs each part's alpha into a height map and runs
// diffuse + specular lighting over it, so rims, rivets, ornament and
// numeral all catch the same top-left light and shade away from it.
// Wear is layered on top: hammered grain and low-frequency patina on the
// metal, grime pooled in the bevel groove, a few scratches across the
// rim, and mottled lacquer on the enamel.
//
// The ornament is the shared SVG file loaded as an <image> inside an
// alpha mask (the file paints its silhouette in currentColor over a
// transparent ground). The metal fill is masked by it and the lighting
// filter sits on the parent <g>, because SVG applies a filter before a
// mask on the same element.
//
// SVG presentation attributes are written hyphenated (stop-color, not
// stopColor): Preact core sets attribute names verbatim, and camelCase
// names are silently ignored by the SVG DOM.

const ORNAMENT_HREF = '/ornaments/viking-style1-007.svg';
const GRAIN_HREF = '/tex-noise.webp';

// Shield outline and two concentric insets, in a 100 x 170 box. The
// top edge arches so the flat hammer head sits under a matching curve.
const OUTER = 'M8 8 Q50 -2 92 8 L92 92 Q92 130 50 166 Q8 130 8 92 Z';
const BEVEL = 'M12.5 12 Q50 3.5 87.5 12 L87.5 91 Q87.5 126 50 159.5 Q12.5 126 12.5 91 Z';
const FIELD = 'M16 14.5 Q50 6.5 84 14.5 L84 90 Q84 123 50 154.5 Q16 123 16 90 Z';

// Ornament box. Width chosen so the file's 614:1100 aspect lands the
// pendant's bottom roundel just above the numeral boss.
const ORNAMENT = { x: 20, y: 17, w: 60, h: 107.5 };

// Engraved rings behind the ornament, spaced tighter toward the centre.
const GUILLOCHE_RADII = [10, 17, 25, 34, 44, 55, 67, 80];

// Rivets where the rim changes direction.
const RIVETS = [[14, 15], [86, 15], [11, 70], [89, 70]];

// Scratches across the rim: [x1, y1, x2, y2].
const SCRATCHES = [
  [9, 40, 15, 52], [86, 30, 91, 41], [10, 100, 13, 113], [88, 96, 84, 111],
  [30, 6, 42, 5], [22, 140, 28, 147], [78, 142, 71, 150], [58, 3, 66, 3.5],
];

export default function TrophyBadge({ rank, size = '5cqw', className = '', style = {} }) {
  const metal = TROPHY_METALS[rank] || TROPHY_METALS[3];
  const id = `tb${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const champion = rank === 1;
  const u = (name) => `url(#${id}-${name})`;

  return (
    <div
      aria-label={t('{metal} trophy', { metal: t(metal.name) })}
      className={`relative shrink-0 ${className}`}
      style={{
        width: size,
        aspectRatio: '100 / 170',
        filter: [
          'drop-shadow(0 0.45cqw 0.7cqw rgba(0,0,0,0.75))',
          champion ? `drop-shadow(0 0 0.7cqw ${metal.glow})` : `drop-shadow(0 0 0.35cqw ${metal.glow})`,
        ].join(' '),
        ...style,
      }}
    >
      <svg viewBox="0 0 100 170" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <defs>
          {/* Brushed rim: alternating bright and dark bands along the diagonal. */}
          <linearGradient id={`${id}-rim`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color={metal.hi} />
            <stop offset="0.2" stop-color={metal.light} />
            <stop offset="0.42" stop-color={metal.mid} />
            <stop offset="0.58" stop-color={metal.dark} />
            <stop offset="0.76" stop-color={metal.light} />
            <stop offset="1" stop-color={metal.mid} />
          </linearGradient>
          <linearGradient id={`${id}-orn`} x1="0" y1="0" x2="0.25" y2="1">
            <stop offset="0" stop-color={metal.hi} />
            <stop offset="0.3" stop-color={metal.light} />
            <stop offset="0.55" stop-color={metal.mid} />
            <stop offset="0.78" stop-color={metal.dark} />
            <stop offset="1" stop-color={metal.mid} />
          </linearGradient>
          <radialGradient id={`${id}-enamel`} cx="0.5" cy="0.3" r="0.8">
            <stop offset="0" stop-color={metal.enamelLight} />
            <stop offset="0.75" stop-color={metal.enamelDark} />
            <stop offset="1" stop-color="#000000" />
          </radialGradient>
          <radialGradient id={`${id}-vignette`} cx="0.5" cy="0.45" r="0.6">
            <stop offset="0.55" stop-color="#000000" stop-opacity="0" />
            <stop offset="1" stop-color="#000000" stop-opacity="0.7" />
          </radialGradient>
          <radialGradient id={`${id}-boss`} cx="0.4" cy="0.3" r="0.85">
            <stop offset="0" stop-color={metal.light} />
            <stop offset="0.6" stop-color={metal.mid} />
            <stop offset="1" stop-color={metal.dark} />
          </radialGradient>
          <linearGradient id={`${id}-gloss`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#ffffff" stop-opacity="0.16" />
            <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
          </linearGradient>
          <linearGradient id={`${id}-sheen`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stop-color="#ffffff" stop-opacity="0" />
            <stop offset="0.5" stop-color="#ffffff" stop-opacity="0.2" />
            <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
          </linearGradient>

          {/* Relief lighting. Alpha, blurred, is the height map; diffuse
              shades the slopes, specular adds the hot edge highlight. */}
          <filter id={`${id}-light`} x="-10%" y="-10%" width="120%" height="120%" color-interpolation-filters="sRGB">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.1" result="bump" />
            <feDiffuseLighting in="bump" surfaceScale="4" diffuseConstant="1.3" lighting-color="#ffffff" result="diffuse">
              <feDistantLight azimuth="225" elevation="52" />
            </feDiffuseLighting>
            <feComposite in="diffuse" in2="SourceGraphic" operator="arithmetic" k1="1" k2="0" k3="0" k4="0" result="shaded" />
            <feSpecularLighting in="bump" surfaceScale="4" specularConstant="0.9" specularExponent="20" lighting-color="#ffffff" result="spec">
              <feDistantLight azimuth="225" elevation="45" />
            </feSpecularLighting>
            <feComposite in="spec" in2="SourceAlpha" operator="in" result="specIn" />
            <feComposite in="shaded" in2="specIn" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="lit" />
            <feComposite in="lit" in2="SourceAlpha" operator="in" />
          </filter>
          {/* Hammered grain over the metal. */}
          <filter id={`${id}-hammer`} x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="3" seed="7" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 1.4 -0.35" />
          </filter>
          {/* Patina: broad tarnish blotches. */}
          <filter id={`${id}-patina`} x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.05 0.08" numOctaves="4" seed="3" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 2.2 -0.9" />
          </filter>
          {/* Mottled lacquer for the enamel. */}
          <filter id={`${id}-mottle`} x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.035 0.05" numOctaves="5" seed="11" result="noise" />
            <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1.6 -0.55" />
          </filter>
          <filter id={`${id}-blur`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.1" />
          </filter>
          <filter id={`${id}-softblur`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="0.6" />
          </filter>
          {/* Enamel sits below the rim: shadow cast inward from the lip. */}
          <filter id={`${id}-inset`} x="-20%" y="-20%" width="140%" height="140%">
            <feOffset dy="2.5" />
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite operator="out" in="SourceGraphic" in2="blur" result="inverse" />
            <feFlood flood-color="#000000" flood-opacity="0.85" result="ink" />
            <feComposite operator="in" in="ink" in2="inverse" result="shadow" />
            <feComposite operator="over" in="shadow" in2="SourceGraphic" />
          </filter>

          <clipPath id={`${id}-outer`}><path d={OUTER} /></clipPath>
          <clipPath id={`${id}-field`}><path d={FIELD} /></clipPath>
          {/* The rim annulus: outer shape minus the field. */}
          <clipPath id={`${id}-rimclip`}><path d={`${OUTER} ${FIELD}`} clip-rule="evenodd" /></clipPath>
          <mask id={`${id}-ornmask`} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="170" style={{ maskType: 'alpha' }}>
            <image href={ORNAMENT_HREF} x={ORNAMENT.x} y={ORNAMENT.y} width={ORNAMENT.w} height={ORNAMENT.h} preserveAspectRatio="xMidYMid meet" />
          </mask>
        </defs>

        {/* Pendant loop behind the rim. */}
        <g filter={u('light')}>
          <circle cx="50" cy="5" r="6.5" fill="none" stroke={u('rim')} stroke-width="3.2" />
        </g>
        <circle cx="50" cy="5" r="6.5" fill="none" stroke="#000000" stroke-width="3.6" stroke-opacity="0.35" filter={u('softblur')} clip-path={u('outer')} />

        {/* Rim: lit metal, then wear layered on top. */}
        <g filter={u('light')}>
          <path d={OUTER} fill={u('rim')} />
        </g>
        <g clip-path={u('rimclip')}>
          <rect x="0" y="0" width="100" height="170" filter={u('hammer')} opacity="0.42" style={{ mixBlendMode: 'overlay' }} />
          <rect x="0" y="0" width="100" height="170" filter={u('patina')} fill={metal.deep} opacity="0.38" style={{ mixBlendMode: 'multiply' }} />
          {SCRATCHES.map(([x1, y1, x2, y2], i) => (
            <g key={i}>
              <line x1={x1} y1={y1 + 0.5} x2={x2} y2={y2 + 0.5} stroke={metal.hi} stroke-width="0.35" stroke-opacity="0.45" />
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={metal.deep} stroke-width="0.4" stroke-opacity="0.7" />
            </g>
          ))}
        </g>
        <path d={OUTER} fill="none" stroke={metal.deep} stroke-width="0.9" stroke-opacity="0.9" />
        {/* Grime pooled in the bevel groove, then the groove itself. */}
        <path d={BEVEL} fill="none" stroke={metal.deep} stroke-width="2.6" stroke-opacity="0.6" filter={u('blur')} />
        <path d={BEVEL} fill="none" stroke={metal.deep} stroke-width="0.9" stroke-opacity="0.95" />
        <path d={BEVEL} fill="none" stroke={metal.hi} stroke-width="0.45" stroke-opacity="0.5" transform="translate(0 0.7)" />

        {/* Enamel field and its ageing. */}
        <path d={FIELD} fill={u('enamel')} filter={u('inset')} />
        <g clip-path={u('field')}>
          <rect x="0" y="0" width="100" height="170" filter={u('mottle')} fill={metal.enamelLight} opacity="0.8" style={{ mixBlendMode: 'screen' }} />
          {GUILLOCHE_RADII.map((r) => (
            <circle key={r} cx="50" cy="78" r={r} fill="none" stroke={metal.light} stroke-width="0.35" stroke-opacity="0.1" />
          ))}
          <image href={GRAIN_HREF} x="16" y="6" width="68" height="150" preserveAspectRatio="xMidYMid slice" opacity="0.45" style={{ mixBlendMode: 'overlay' }} />
          <rect x="0" y="0" width="100" height="170" fill={u('vignette')} />
          <ellipse cx="50" cy="8" rx="46" ry="30" fill={u('gloss')} />
        </g>
        <path d={FIELD} fill="none" stroke={metal.hi} stroke-width="0.4" stroke-opacity="0.3" />

        {RIVETS.map(([cx, cy]) => (
          <g key={`${cx}-${cy}`}>
            <circle cx={cx} cy={cy + 0.7} r="2" fill="#000000" opacity="0.6" filter={u('softblur')} />
            <g filter={u('light')}>
              <circle cx={cx} cy={cy} r="1.8" fill={metal.mid} />
            </g>
          </g>
        ))}

        {/* Ornament: shadow cast onto the enamel, then the lit metal. */}
        <rect x="0" y="0" width="100" height="170" fill="#000000" opacity="0.8" mask={u('ornmask')} transform="translate(0.6 1.6)" filter={u('blur')} />
        <g filter={u('light')}>
          <rect x="0" y="0" width="100" height="170" fill={u('orn')} mask={u('ornmask')} />
        </g>

        {/* Numeral boss at the point. */}
        <circle cx="50" cy="141" r="12.5" fill="#000000" opacity="0.7" filter={u('blur')} />
        <g filter={u('light')}>
          <circle cx="50" cy="139" r="12" fill={u('boss')} />
        </g>
        <circle cx="50" cy="139" r="9" fill={u('enamel')} stroke={metal.deep} stroke-width="0.9" />
        <circle cx="50" cy="139" r="9" fill="none" stroke={metal.hi} stroke-width="0.4" stroke-opacity="0.35" transform="translate(0 0.6)" />
        <g filter={u('light')}>
          <text
            x="50" y="139.5" text-anchor="middle" dominant-baseline="central"
            font-size={rank === 3 ? 9.5 : 11} font-weight="700" letter-spacing="0.4"
            fill={metal.light}
          >
            {ROMAN_RANKS[rank] || rank}
          </text>
        </g>

        {/* Fixed light sweep across the whole badge. */}
        <g clip-path={u('outer')} style={{ mixBlendMode: 'screen' }}>
          <rect x="-40" y="-20" width="60" height="220" fill={u('sheen')} transform="skewX(-18)" opacity="0.5" />
        </g>
      </svg>
    </div>
  );
}
